import * as React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

const CleanTable = React.forwardRef(({ className, ...props }, ref) => (
  <div className="relative w-full">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
));
CleanTable.displayName = "CleanTable";

const CleanTableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn("border-b border-gray-200", className)}
    {...props}
  />
));
CleanTableHeader.displayName = "CleanTableHeader";

const CleanTableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("", className)} {...props} />
));
CleanTableBody.displayName = "CleanTableBody";

const CleanTableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn("border-b border-gray-200 last:border-b-0", className)}
    {...props}
  />
));
CleanTableRow.displayName = "CleanTableRow";

const CleanTableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-12 px-4 text-left align-middle font-medium text-gray-500 text-sm",
      className
    )}
    {...props}
  />
));
CleanTableHead.displayName = "CleanTableHead";

const CleanTableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn("px-4 py-4 align-middle text-gray-900", className)}
    {...props}
  />
));
CleanTableCell.displayName = "CleanTableCell";

// New expandable row components
const CleanTableExpandableRow = React.forwardRef(
  ({ className, isExpanded, onToggle, children, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b border-gray-200 last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors",
        isExpanded && "bg-gray-50",
        className
      )}
      onClick={onToggle}
      {...props}
    >
      {children}
    </tr>
  )
);
CleanTableExpandableRow.displayName = "CleanTableExpandableRow";

const CleanTableExpandIcon = React.forwardRef(
  ({ className, isExpanded, ...props }, ref) => (
    <td
      ref={ref}
      className={cn("px-4 py-4 align-middle text-gray-900 w-8", className)}
      {...props}
    >
      <div className="flex items-center justify-center">
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        )}
      </div>
    </td>
  )
);
CleanTableExpandIcon.displayName = "CleanTableExpandIcon";

const CleanTableExpandedContent = React.forwardRef(
  ({ className, colSpan, children, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn("border-b border-gray-200 last:border-b-0", className)}
      {...props}
    >
      <td colSpan={colSpan} className="px-0 py-0 bg-gray-50/50">
        <div className="px-4 py-4">{children}</div>
      </td>
    </tr>
  )
);
CleanTableExpandedContent.displayName = "CleanTableExpandedContent";

// Hook for managing expandable table state
const useExpandableTable = (initialExpanded = new Set()) => {
  const [expandedRows, setExpandedRows] = React.useState(initialExpanded);

  const toggleRow = React.useCallback((rowId) => {
    setExpandedRows((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(rowId)) {
        newExpanded.delete(rowId);
      } else {
        newExpanded.add(rowId);
      }
      return newExpanded;
    });
  }, []);

  const isExpanded = React.useCallback(
    (rowId) => {
      return expandedRows.has(rowId);
    },
    [expandedRows]
  );

  const expandAll = React.useCallback((rowIds) => {
    setExpandedRows(new Set(rowIds));
  }, []);

  const collapseAll = React.useCallback(() => {
    setExpandedRows(new Set());
  }, []);

  return {
    expandedRows,
    toggleRow,
    isExpanded,
    expandAll,
    collapseAll,
  };
};

export {
  CleanTable,
  CleanTableHeader,
  CleanTableBody,
  CleanTableHead,
  CleanTableRow,
  CleanTableCell,
  CleanTableExpandableRow,
  CleanTableExpandIcon,
  CleanTableExpandedContent,
  useExpandableTable,
};
